import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import axios from "axios";

const CreateOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    product: "",
    deliveryTime: "",
    firmName: "",
    quantity: "",
    price: "",
    totalPrice: "",
  });
  const [dieShapes, setDieShapes] = useState([]);
  const [selectedDieShape, setSelectedDieShape] = useState("");

  const [userDetails, setUserDetails] = useState(null);
  const [minimumQuantity, setMinimumQuantity] = useState(0);
  const [wallet, setWallet] = useState(0);
  const [insufficientBalance, setInsufficientBalance] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [businessProductFile, setBusinessProductFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const [quantityWarning, setQuantityWarning] = useState(false);

  const [loading, setLoading] = useState(true);
  const [productOptions, setProductOptions] = useState({});
  const [customOptions, setCustomOptions] = useState({
    paperType: "",
    flapOpening: "",
    windowCutting: "",
    printing: "",
    laminationType: "",
  });
  const [checkboxOptions, setCheckboxOptions] = useState({
    isGoldFoil: false,
    isGoldColor: false,
    isSpotUV: false,
    isMattelColor: false,
    isTextureType: false,
    isBinding: false,
    isSize: false,
  });
  const [textureTypes, setTextureTypes] = useState([]);
  const [showWarning, setShowWarning] = useState(false);
  const [productDetails, setProductDetails] = useState(null);

  // Cleanup function for file preview URLs
  const cleanupFilePreview = useCallback(() => {
    if (filePreviewUrl) {
      URL.revokeObjectURL(filePreviewUrl);
      setFilePreviewUrl(null);
    }
  }, [filePreviewUrl]);

  // Initialize data on component mount
  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get product details
        const { data: product } = await axios.get(
          `https://api.vyapakprint.com/api/businessProducts/getBusinessProductById/${id}`
        );
        if (product.isDieShape) {
          const { data: shapes } = await axios.get(
            "https://api.vyapakprint.com/api/dieshapes"
          );
          setDieShapes(shapes);
          setSelectedDieShape(shapes?.[0]?.name || ""); // default to first if exists
        }

        // Get user details
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error(
            "Authentication token not found. Please log in again."
          );
        }

        const decoded = jwtDecode(token);
        const userId = decoded?.user?.userId;
        if (!userId) {
          throw new Error("Invalid authentication token. Please log in again.");
        }

        const { data: user } = await axios.get(
          `https://api.vyapakprint.com/api/distribution/${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // Update state with fetched data
        setUserDetails(user);
        setWallet(user.wallet || 0);
        setMinimumQuantity(product.minimumQuantity || 1);
        setProductDetails(product);

        // Set form data with product info
        setFormData((prev) => ({
          ...prev,
          product: product.name || "",
          deliveryTime: product.shippingTime || "",
          quantity: String(product.minimumQuantity || 1),
          price: String(product.prices?.[user.userType] || 0),
        }));

        // Set product options
        setProductOptions({
          paperTypes: product.paperTypes || [],
          flapOpenings: product.flapOpenings || [],
          windowCuttings: product.windowCuttings || [],
          printings: product.printings || [],
          laminationTypes: product.laminationTypes || [],
        });

        // Initialize customization options with first available option
        const initialCustomOptions = {
          paperType: product.paperTypes?.[0] || "",
          flapOpening: product.flapOpenings?.[0] || "",
          windowCutting: product.windowCuttings?.[0] || "",
          printing: product.printings?.[0] || "",
          laminationType: product.laminationTypes?.[0] || "",
        };
        setCustomOptions(initialCustomOptions);
      } catch (err) {
        console.error("Initialization error:", err);
        const errorMessage =
          err.response?.data?.message || err.message || "Failed to load data";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      initializeData();
    }
  }, [id]);

  // Calculate total price when quantity or price changes
  useEffect(() => {
    const quantity = parseFloat(formData.quantity) || 0;
    const price = parseFloat(formData.price) || 0;
    const totalPrice = quantity * price;

    setFormData((prev) => ({
      ...prev,
      totalPrice: totalPrice.toFixed(2),
    }));
  }, [formData.quantity, formData.price]);

  // Cleanup file preview URL on unmount
  useEffect(() => {
    return cleanupFilePreview;
  }, [cleanupFilePreview]);

  const handleCheckboxChange = (key) => {
    setCheckboxOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleTextureTypesChange = (value) => {
    const types = value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
    setTextureTypes(types);
  };

  const handleChange = (e) => {
    const { id, value } = e.target;

    if (id === "quantity") {
      const numValue = parseInt(value) || 0;

      // Always allow the user to enter what they want
      setFormData((prev) => ({
        ...prev,
        [id]: String(numValue),
      }));

      // Show warning if not a multiple of minimumQuantity
      if (numValue % minimumQuantity !== 0) {
        setQuantityWarning(true);
      } else {
        setQuantityWarning(false);
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [id]: value,
      }));
    }
  };

  const handleCustomizationChange = (key, value) => {
    setCustomOptions((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (!file) {
      setBusinessProductFile(null);
      cleanupFilePreview();
      return;
    }

    // Validate file type
    const allowedTypes = ["application/pdf"];
    const fileExtension = file.name.toLowerCase().split(".").pop();
    const isValidType =
      allowedTypes.includes(file.type) ||
      ["pdf", "cdr"].includes(fileExtension);

    if (!isValidType) {
      alert("Please upload only PDF or CDR files");
      e.target.value = "";
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert("File size must be less than 10MB");
      e.target.value = "";
      return;
    }

    setBusinessProductFile(file);
    cleanupFilePreview();

    // Create preview URL for PDF files
    if (file.type === "application/pdf") {
      const url = URL.createObjectURL(file);
      setFilePreviewUrl(url);
    }
  };

  const resetForm = () => {
    setFormData({
      product: productDetails?.name || "",
      deliveryTime: productDetails?.shippingTime || "",
      firmName: "",
      quantity: String(minimumQuantity),
      price: String(productDetails?.prices?.[userDetails?.userType] || 0),
      totalPrice: "",
    });
    setCustomOptions({
      paperType: productOptions.paperTypes?.[0] || "",
      flapOpening: productOptions.flapOpenings?.[0] || "",
      windowCutting: productOptions.windowCuttings?.[0] || "",
      printing: productOptions.printings?.[0] || "",
      laminationType: productOptions.laminationTypes?.[0] || "",
    });
    setCheckboxOptions({
      isGoldFoil: false,
      isGoldColor: false,
      isSpotUV: false,
      isMattelColor: false,
      isTextureType: false,
      isBinding: false,
      isSize: false,
    });
    setTextureTypes([]);
    setBusinessProductFile(null);
    cleanupFilePreview();
    setInsufficientBalance(false);

    // Clear file input
    const fileInput = document.getElementById("file");
    if (fileInput) fileInput.value = "";
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.firmName.trim()) {
      alert("Please enter a firm name");
      return;
    }

    if (!businessProductFile) {
      alert("Please upload a PDF or CDR file");
      return;
    }

    const totalPrice = parseFloat(formData.totalPrice) || 0;
    if (totalPrice > wallet) {
      setInsufficientBalance(true);
      return;
    }

    setInsufficientBalance(false);
    setShowWarning(true);
  };

  const confirmAndCreate = async () => {
    setShowWarning(false);
    setIsSubmitting(true);

    try {
      const orderAmount = parseFloat(formData.totalPrice) || 0;
      const newBalance = wallet - orderAmount;

      const orderDetails = new FormData();

      // Basic order details
      orderDetails.append("productId", id);
      orderDetails.append("productName", formData.product); // ✅ send product name
      orderDetails.append("userId", userDetails._id);

      orderDetails.append("userDetails", JSON.stringify(userDetails));
      orderDetails.append("totalPrice", orderAmount.toString());
      orderDetails.append("quantity", formData.quantity);
      orderDetails.append("firmName", formData.firmName.trim());
      orderDetails.append("orderStatus", "pending");
      orderDetails.append("orderDate", new Date().toISOString());

      // File upload
      if (businessProductFile) {
        orderDetails.append("file", businessProductFile);
      }

      // Wallet history
      const walletHistoryData = {
        amount: orderAmount,
        remark: "Order Payment",
        receiptId: `REC-${Date.now()}`,
        date: new Date().toISOString(),
        previousBalance: wallet,
        newBalance: newBalance,
      };
      orderDetails.append("walletHistory", JSON.stringify(walletHistoryData));

      // Customizations
      const customizationsData = {
        paperType: customOptions.paperType || "",
        flapOpening: customOptions.flapOpening || "",
        windowCutting: customOptions.windowCutting || "",
        printing: customOptions.printing || "",
        laminationType: customOptions.laminationType || "",
        ...checkboxOptions,
        textureTypes: checkboxOptions.isTextureType ? textureTypes : [],
        dieShape: productDetails?.isDieShape ? selectedDieShape : "", // ← add this line
      };
      orderDetails.append("customizations", JSON.stringify(customizationsData));

      // Create order
      const response = await axios.post(
        "https://api.vyapakprint.com/api/businessOrders/createBusinessOrder",
        orderDetails,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status === 201) {
        const orderId = response.data?.BusinessOrder?._id;

        // Update wallet
        await axios.put(
          `https://api.vyapakprint.com/api/distribution/${userDetails._id}/wallet`,
          {
            wallet: orderAmount,
            remark: `Order placed with ID: ${orderId}`,
            receiptId: orderId,
            transactionType: "debit",
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        alert(`Order ${orderId} created successfully!`);
        resetForm();
        navigate("/"); // Navigate to orders page instead of home
      }
    } catch (err) {
      console.error("Order creation error:", err);
      const errorMessage =
        err.response?.data?.message || "Error submitting the order";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Create a mapping from productOptions keys to customOptions keys
  const optionKeyMapping = {
    paperTypes: "paperType",
    flapOpenings: "flapOpening",
    windowCuttings: "windowCutting",
    printings: "printing",
    laminationTypes: "laminationType",
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading order form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6 min-h-screen">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <h3 className="font-bold">Error</h3>
          <p>{error}</p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 min-h-screen">
      {/* Warning Modal */}
      {showWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg shadow-xl p-4 max-w-3xl w-full text-center max-h-[90vh] overflow-y-auto">
            <h3 className="text-base font-semibold mb-4 text-gray-800">
              Order Confirmation
            </h3>

            {/* Two-column layout: video and image */}
            <div className="flex flex-col md:flex-row gap-4 items-center justify-center mb-4">
              {/* Video */}
              <div className="w-full md:w-1/2">
                <div className="relative w-full h-0 pb-[56.25%] rounded-md overflow-hidden shadow-md">
                  <iframe
                    src="https://www.youtube.com/embed/FydujDkf9Y8?autoplay=0&controls=1&modestbranding=1&rel=0"
                    title="Order Info Video"
                    className="absolute top-0 left-0 w-full h-full border-0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Watch this short video before proceeding.
                </p>
              </div>

              {/* Image */}
              <div className="w-full md:w-1/2 flex justify-center">
                <img
                  src="https://ik.imagekit.io/devhm/WhatsApp%20Image%202025-07-01%20at%2019.51.47.jpeg?updatedAt=1751379861023"
                  alt="Order Warning"
                  className="w-full max-w-xs rounded-md shadow"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col md:flex-row justify-center gap-3 mt-3">
              <button
                onClick={() => setShowWarning(false)}
                className="px-4 py-1.5 text-sm bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmAndCreate}
                disabled={isSubmitting}
                className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:bg-blue-400"
              >
                {isSubmitting ? "Processing..." : "Confirm & Place"}
              </button>
            </div>
          </div>
        </div>
      )}

      <h1 className="text-3xl font-bold mb-8">Create Order</h1>

      <div className="bg-white p-8 rounded-lg shadow-lg border">
        <form
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          onSubmit={handleSubmit}
        >
          {/* Product Field */}
          <div>
            <label
              htmlFor="product"
              className="block text-sm font-medium text-gray-700"
            >
              Product
            </label>
            <input
              id="product"
              type="text"
              value={formData.product}
              readOnly
              className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm text-gray-900 bg-gray-50"
            />
          </div>

          {/* Delivery Time Field */}
          <div>
            <label
              htmlFor="deliveryTime"
              className="block text-sm font-medium text-gray-700"
            >
              Delivery Time
            </label>
            <input
              id="deliveryTime"
              type="text"
              value={formData.deliveryTime}
              readOnly
              className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm text-gray-900 bg-gray-50"
            />
          </div>

          {/* Product Description */}
          {productDetails?.description && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Description
              </label>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-gray-800 leading-relaxed">
                  {productDetails.description}
                </p>
              </div>
            </div>
          )}

          {/* Additional Product Information */}
          {(productDetails?.dimensions ||
            productDetails?.weight ||
            productDetails?.material) && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Product Information
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                {productDetails.category && (
                  <div>
                    <span className="text-xs font-medium text-gray-600">
                      Category
                    </span>
                    <p className="text-sm text-gray-900">
                      {productDetails.category}
                    </p>
                  </div>
                )}
                {productDetails.dimensions && (
                  <div>
                    <span className="text-xs font-medium text-gray-600">
                      Dimensions
                    </span>
                    <p className="text-sm text-gray-900">
                      {productDetails.dimensions}
                    </p>
                  </div>
                )}
                {productDetails.weight && (
                  <div>
                    <span className="text-xs font-medium text-gray-600">
                      Weight
                    </span>
                    <p className="text-sm text-gray-900">
                      {productDetails.weight}
                    </p>
                  </div>
                )}
                {productDetails.material && (
                  <div>
                    <span className="text-xs font-medium text-gray-600">
                      Material
                    </span>
                    <p className="text-sm text-gray-900">
                      {productDetails.material}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quantity Field */}
          <div>
          
            <div>
              <label
                htmlFor="quantity"
                className="block text-sm font-medium text-gray-700"
              >
                Quantity (Min: {minimumQuantity})
              </label>
              <input
                id="quantity"
                type="number"
                value={formData.quantity}
                onChange={handleChange}
                min={minimumQuantity}
                required
                className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              {quantityWarning && (
                <p className="text-sm text-yellow-600 mt-1">
                  ⚠ Quantity must be a multiple of {minimumQuantity}
                </p>
              )}
            </div>
          </div>

          {/* Price Field */}
          <div>
            <label
              htmlFor="price"
              className="block text-sm font-medium text-gray-700"
            >
              Price Per Unit
            </label>
            <input
              id="price"
              type="text"
              value={formData.price}
              readOnly
              className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm text-gray-900 bg-gray-50"
            />
          </div>

          {/* Total Price Field */}
          <div>
            <label
              htmlFor="totalPrice"
              className="block text-sm font-medium text-gray-700"
            >
              Total Price
            </label>
            <input
              id="totalPrice"
              type="text"
              value={formData.totalPrice}
              readOnly
              className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm text-gray-900 bg-gray-50"
            />
          </div>

          {/* Firm Name Field */}
          <div>
            <label
              htmlFor="firmName"
              className="block text-sm font-medium text-gray-700"
            >
              Firm Name <span className="text-red-500">*</span>
            </label>
            <input
              id="firmName"
              type="text"
              value={formData.firmName}
              onChange={handleChange}
              required
              className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Checkbox Options */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Additional Options
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { label: "Gold Foil", key: "isGoldFoil" },
                { label: "Gold Color", key: "isGoldColor" },
                { label: "Spot UV", key: "isSpotUV" },
                { label: "Mattel Color", key: "isMattelColor" },
                { label: "Binding", key: "isBinding" },
                { label: "Size Customization", key: "isSize" },
                { label: "Texture Type", key: "isTextureType" },
              ].map(({ label, key }) => (
                <label key={key} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={checkboxOptions[key]}
                    onChange={() => handleCheckboxChange(key)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{label}</span>
                </label>
              ))}
            </div>
          </div>
          {productDetails?.isDieShape && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Select Die Shape
              </label>
              <select
                value={selectedDieShape}
                onChange={(e) => setSelectedDieShape(e.target.value)}
                className="w-full mt-1 p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {dieShapes.map((shape) => (
                  <option key={shape._id} value={shape.name}>
                    {shape.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Texture Types Input */}
          {checkboxOptions.isTextureType && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Texture Types (comma-separated)
              </label>
              <input
                type="text"
                onChange={(e) => handleTextureTypesChange(e.target.value)}
                placeholder="e.g., Leather, Linen, Canvas"
                className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          )}

          {/* Customization Options */}
          {Object.entries(productOptions).map(
            ([key, values]) =>
              values.length > 0 && (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 capitalize">
                    {key.replace(/([A-Z])/g, " $1").replace(/s$/, "")}
                  </label>
                  <select
                    value={customOptions[optionKeyMapping[key]] || ""}
                    onChange={(e) =>
                      handleCustomizationChange(
                        optionKeyMapping[key],
                        e.target.value
                      )
                    }
                    className="w-full mt-1 p-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">
                      Select {key.replace(/([A-Z])/g, " $1").replace(/s$/, "")}
                    </option>
                    {values.map((v) => (
                      <option key={v} value={v}>
                        {v}
                      </option>
                    ))}
                  </select>
                </div>
              )
          )}

          {/* File Upload Section */}
          <div className="md:col-span-2">
            <label
              htmlFor="file"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Upload PDF/CDR File <span className="text-red-500">*</span>
            </label>
            <div className="space-y-4">
              <div className="flex items-center justify-center w-full">
                <label
                  htmlFor="file"
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg
                      className="w-8 h-8 mb-4 text-gray-500"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 20 16"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                      />
                    </svg>
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">Click to upload</span> or
                      drag and drop
                    </p>
                    <p className="text-xs text-gray-500">
                      PDF or CDR files only (Max 10MB)
                    </p>
                  </div>
                  <input
                    id="file"
                    type="file"
                    accept=".pdf,.cdr"
                    onChange={handleFileChange}
                    className="hidden"
                    required
                  />
                </label>
              </div>

              {/* File Information Display */}
              {businessProductFile && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    {/* File Info */}
                    <div className="flex items-center gap-3 flex-1">
                      <div className="p-1.5 bg-blue-100 rounded-md">
                        {businessProductFile.type === "application/pdf" ? (
                          <svg
                            className="w-5 h-5 text-red-600"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M4 18h12V6h-4V2H4v16zm8-14v4h4l-4-4z" />
                          </svg>
                        ) : (
                          <svg
                            className="w-5 h-5 text-blue-600"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M4 18h12V6h-4V2H4v16zm8-14v4h4l-4-4z" />
                          </svg>
                        )}
                      </div>
                      <div className="text-sm">
                        <p className="font-medium text-gray-800 truncate max-w-[150px]">
                          {businessProductFile.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(businessProductFile.size / 1024 / 1024).toFixed(2)}{" "}
                          MB
                        </p>
                      </div>
                    </div>

                    {/* Compact Preview */}
                    {filePreviewUrl &&
                      businessProductFile?.type === "application/pdf" && (
                        <div className="w-[120px] h-[150px] border border-gray-200 rounded-md overflow-hidden">
                          <iframe
                            src={`${filePreviewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                            className="w-full h-full border-0"
                            title="PDF Preview"
                          />
                        </div>
                      )}

                    {/* Remove File Button */}
                    <button
                      type="button"
                      onClick={() => {
                        setBusinessProductFile(null);
                        cleanupFilePreview();
                        document.getElementById("file").value = "";
                      }}
                      className="text-red-600 hover:text-red-800 self-start md:self-center"
                      title="Remove File"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Optional Text Below */}
                  {filePreviewUrl &&
                    businessProductFile?.type === "application/pdf" && (
                      <p className="text-[11px] text-gray-500 mt-1 text-center">
                        Preview only – full file will be uploaded.
                      </p>
                    )}
                </div>
              )}

              {/* CDR File Info */}
              {businessProductFile &&
                businessProductFile.name.toLowerCase().endsWith(".cdr") && (
                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center">
                      <svg
                        className="w-5 h-5 text-yellow-600 mr-2"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <p className="text-sm text-yellow-800">
                        CDR file uploaded successfully. Preview not available
                        for CDR files.
                      </p>
                    </div>
                  </div>
                )}
            </div>
          </div>

          {/* Insufficient Balance Warning */}
          {insufficientBalance && (
            <div className="md:col-span-2">
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div>
                    <p className="font-medium">Insufficient wallet balance</p>
                    <p className="text-sm">
                      Current balance: ₹{wallet.toFixed(2)} | Required: ₹
                      {formData.totalPrice}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Wallet Balance Info */}
          <div className="md:col-span-2">
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" />
                  </svg>
                  <span className="font-medium">
                    Current Wallet Balance: ₹{wallet.toFixed(2)}
                  </span>
                </div>
                {formData.totalPrice && (
                  <span className="text-sm">
                    Remaining after order: ₹
                    {(wallet - parseFloat(formData.totalPrice)).toFixed(2)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="md:col-span-2 flex justify-end gap-4 pt-4">
            <button
              type="button"
              onClick={resetForm}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              Reset Form
            </button>
            <button
              type="submit"
              disabled={isSubmitting || insufficientBalance}
              className={`px-6 py-3 rounded-lg text-white transition-colors font-medium ${
                isSubmitting || insufficientBalance
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Submitting...
                </div>
              ) : (
                "Submit Order"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOrder;
